// Console adapters over @vd/ui. Screens import from here so the component calls stay short;
// every visual comes from the design system.
import {
  cn,
  type InputProps,
  SegmentedControl,
  Surface,
  TabsList,
  TabsTrigger,
  type TextareaProps,
  Tooltip,
  Badge as VdBadge,
  Button as VdButton,
  Dialog as VdDialog,
  EmptyState as VdEmptyState,
  Field as VdField,
  Input as VdInput,
  Kbd as VdKbd,
  PageHeader as VdPageHeader,
  Select as VdSelect,
  Spinner as VdSpinner,
  Tabs as VdTabs,
  Textarea as VdTextarea,
} from "@vd/ui";
import { AlertTriangle, Inbox } from "lucide-react";
import {
  type ButtonHTMLAttributes,
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";
import { errorMessage } from "../api/client";
import { Crumbs } from "./Crumbs";

export const cx = cn;

// Buttons

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "tinted";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md";
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", busy, type = "button", ...rest },
  ref,
) {
  return <VdButton ref={ref} type={type} variant={variant} size={size} loading={busy ?? false} {...rest} />;
});

// Inputs

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ invalid, ...props }, ref) {
  return <VdInput ref={ref} invalid={invalid || props["aria-invalid"] === true} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(props, ref) {
  return <VdTextarea ref={ref} {...props} />;
});

// Radix Select can't hold an empty value, so "" (meaning "all" or "none") travels as a sentinel.
const EMPTY = "__empty__";

export interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: { value: string; label: string; hint?: string; disabled?: boolean }[];
  "aria-label"?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function Select({ value, onValueChange, options, ...rest }: SelectProps) {
  // Nothing picked and no option stands for "": show the placeholder.
  const blank = value === "" && !options.some((o) => o.value === "");
  return (
    <VdSelect
      {...rest}
      value={blank ? "" : value === "" ? EMPTY : value}
      onValueChange={(v) => onValueChange(v === EMPTY ? "" : v)}
      options={options.map((o) => ({
        value: o.value === "" ? EMPTY : o.value,
        label: o.label,
        ...(o.hint ? { hint: o.hint } : {}),
        ...(o.disabled ? { disabled: true } : {}),
      }))}
    />
  );
}

/** Label above a single control. The control receives the generated id. */
export function Field({
  label,
  error,
  help,
  children,
  className,
}: {
  label: string;
  error?: string | null | undefined;
  help?: string | undefined;
  children: ReactNode;
  className?: string;
}) {
  return (
    <VdField
      label={label}
      {...(error ? { error } : {})}
      {...(help ? { help } : {})}
      {...(className ? { className } : {})}
    >
      {(id) =>
        isValidElement(children) ? cloneElement(children as ReactElement<{ id?: string }>, { id }) : children
      }
    </VdField>
  );
}

export function HelpTip({ text, label = "More information" }: { text: string; label?: string }) {
  return (
    <Tooltip content={text}>
      <button
        type="button"
        aria-label={label}
        className="inline-flex size-3.5 cursor-help items-center justify-center rounded-pill bg-fill-2 text-[9px] text-ink-3 hover:text-ink"
      >
        ?
      </button>
    </Tooltip>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean | undefined;
  id?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "press relative inline-flex h-5 w-9 shrink-0 items-center rounded-pill transition-colors disabled:opacity-45",
        checked ? "bg-accent" : "bg-fill-3",
      )}
    >
      <span
        className={cn(
          "inline-block size-4 rounded-pill bg-white shadow-control transition-transform duration-150",
          checked ? "translate-x-[18px]" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  disabled,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <SegmentedControl
      aria-label={label}
      value={value}
      onValueChange={onChange}
      options={options.map((o) => ({ ...o, ...(disabled ? { disabled } : {}) }))}
    />
  );
}

// Display

export type Tone = "neutral" | "accent" | "ok" | "warn" | "danger" | "info";
const BADGE_TONE = {
  neutral: "neutral",
  accent: "accent",
  ok: "success",
  warn: "warning",
  danger: "danger",
  info: "accent",
} as const;

export function Badge({
  tone = "neutral",
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <VdBadge tone={BADGE_TONE[tone]} className={className}>
      {children}
    </VdBadge>
  );
}

const DOT: Record<Tone, string> = {
  neutral: "bg-ink-3",
  accent: "bg-accent",
  ok: "bg-success",
  warn: "bg-warning",
  danger: "bg-danger",
  info: "bg-accent-ink",
};

export function Dot({ tone, pulse }: { tone: Tone; pulse?: boolean }) {
  return (
    <span
      className={cn("inline-flex size-2 shrink-0 rounded-pill", DOT[tone], pulse && "animate-vd-pulse")}
    />
  );
}

export function Card({
  children,
  className,
  title,
  actions,
}: {
  children: ReactNode;
  className?: string;
  title?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Surface asChild className={cn("flex flex-col", className)}>
      <section>
        {title || actions ? (
          <header className="flex min-h-12 items-center justify-between gap-3 border-hairline border-b px-4 py-2">
            <h2 className="font-medium text-title">{title}</h2>
            {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
          </header>
        ) : null}
        {children}
      </section>
    </Surface>
  );
}

export function PageHeader({
  title,
  actions,
  crumbs,
  children,
}: {
  title: string;
  actions?: ReactNode;
  /** The crumbs over the title: the trail's (Crumbs) unless a page places them itself. */
  crumbs?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <VdPageHeader
      title={title}
      meta={children}
      actions={actions}
      crumbs={crumbs === undefined ? <Crumbs /> : crumbs}
    />
  );
}

export function Spinner({ className }: { className?: string }) {
  return <VdSpinner className={className} />;
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-12 text-ink-3">
      <VdSpinner />
      <span className="text-caption">{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  icon,
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
}) {
  return (
    <VdEmptyState
      icon={icon ?? <Inbox />}
      title={title}
      {...(description ? { description } : {})}
      className="py-12"
    />
  );
}

export function ErrorState({
  error,
  onRetry,
  title = "Couldn't load this",
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}) {
  return (
    <div role="alert">
      <VdEmptyState
        icon={<AlertTriangle />}
        title={title}
        description={errorMessage(error)}
        className="py-12"
        action={
          onRetry ? (
            <Button size="sm" onClick={onRetry}>
              Try again
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}

export function Stat({
  label,
  value,
  tone,
  sub,
}: {
  label: string;
  value: ReactNode;
  tone?: Tone | undefined;
  sub?: ReactNode;
}) {
  const toneText: Partial<Record<Tone, string>> = {
    danger: "text-danger-ink",
    warn: "text-warning",
    ok: "text-success",
  };
  return (
    <Surface className="px-4 py-3">
      <p className="text-caption text-ink-3">{label}</p>
      <p
        className={cn(
          "mt-1 font-display font-medium text-display tnum",
          (tone && toneText[tone]) || "text-ink",
        )}
      >
        {value}
      </p>
      {sub ? <p className="mt-0.5 text-caption text-ink-3">{sub}</p> : null}
    </Surface>
  );
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  tone,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  tone?: "default" | "danger";
}) {
  return (
    <VdDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={title}
      {...(description ? { description } : {})}
      footer={footer}
      size={size}
      {...(tone ? { tone } : {})}
    >
      {children}
    </VdDialog>
  );
}

export function Tabs<T extends string>({
  value,
  tabs,
  onChange,
  label,
}: {
  value: T;
  tabs: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <VdTabs variant="line" value={value} onValueChange={(v) => onChange(v as T)} className="mb-4">
      <TabsList aria-label={label}>
        {tabs.map((t) => (
          <TabsTrigger key={t.value} value={t.value}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </VdTabs>
  );
}

export function Kbd({ keys }: { keys: string }) {
  return <VdKbd keys={keys} size="sm" />;
}
